import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Target } from 'lucide-react';
import { useReadingGoals } from '@/hooks/useReadingGoals';
import { useToast } from '@/hooks/use-toast';

interface ManageReadingGoalDialogProps {
  year?: number;
  currentGoal?: number;
  onGoalUpdated?: () => void;
  trigger?: React.ReactNode;
}

export function ManageReadingGoalDialog({ 
  year = new Date().getFullYear(), 
  currentGoal, 
  onGoalUpdated,
  trigger 
}: ManageReadingGoalDialogProps) {
  const [open, setOpen] = useState(false);
  const [targetBooks, setTargetBooks] = useState(currentGoal?.toString() || '');
  const { loading, createOrUpdateGoal } = useReadingGoals();
  const { toast } = useToast();

  useEffect(() => {
    if (currentGoal !== undefined) {
      setTargetBooks(currentGoal.toString());
    }
  }, [currentGoal]);

  const handleSave = async () => {
    const value = parseInt(targetBooks) || 0;
    if (value < 1) {
      toast({
        variant: "destructive",
        title: "Invalid goal",
        description: "Please enter at least 1 book",
      });
      return;
    }
    await createOrUpdateGoal(year, value);
    setOpen(false);
    onGoalUpdated?.();
  };

  const presets = [12, 24, 36, 52, 100];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Target className="w-4 h-4 mr-2" />
            {currentGoal ? 'Edit Goal' : 'Set Goal'}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set Reading Goal for {year}</DialogTitle>
          <DialogDescription>
            How many books do you want to read this year?
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="target-books">Target Books</Label>
            <Input
              id="target-books"
              type="number"
              min="0"
              max="1000"
              value={targetBooks}
              onChange={(e) => setTargetBooks(e.target.value)}
              placeholder="Enter number of books"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Quick Presets</Label>
            <div className="flex flex-wrap gap-2">
              {presets.map((preset) => (
                <Button
                  key={preset}
                  variant={parseInt(targetBooks) === preset ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setTargetBooks(preset.toString())}
                >
                  {preset} books
                </Button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Goal'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
